import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FREE_MODE } from '../config/appConfig';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MapPin, Calendar, User, Send, ArrowLeft, Building2, Loader2, AlertCircle, Phone, Mail, Lock, CreditCard, MessageSquare, ImageOff } from 'lucide-react';
import { toast } from 'sonner';
import SEO, { getJobPostingSchema, getBreadcrumbSchema } from '../components/SEO';
import { trackOfferResponse } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ImageItem({ src, index, onClick, selected }) {
  const [hasError, setHasError] = useState(false);
  
  if (hasError || !src) {
    return (
      <div className={`aspect-square rounded-lg overflow-hidden border-2 ${selected ? 'border-orange-500' : 'border-transparent'} bg-slate-100 flex items-center justify-center`}>
        <ImageOff className="w-6 h-6 text-slate-300" />
      </div>
    );
  }
  
  return (
    <button 
      onClick={() => onClick(src)}
      className={`aspect-square rounded-lg overflow-hidden border-2 ${selected ? 'border-orange-500' : 'border-transparent'}`}
    >
      <img 
        src={src} 
        alt={`Zdjęcie ${index + 1}`} 
        className="w-full h-full object-cover"
        onError={() => setHasError(true)}
      />
    </button>
  );
}

// Komponent dla głównego zdjęcia z fallback
function MainImage({ src, alt }) {
  const [hasError, setHasError] = useState(false);
  
  if (hasError || !src) {
    return (
      <div className="w-full rounded-lg max-h-96 bg-slate-100 flex items-center justify-center" style={{height: '300px'}}>
        <ImageOff className="w-12 h-12 text-slate-300" />
      </div>
    );
  }
  
  return (
    <img 
      src={src} 
      alt={alt} 
      className="w-full rounded-lg max-h-96 object-contain bg-slate-100"
      onError={() => setHasError(true)}
    />
  );
}

export default function OfferDetails() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isCompany, token } = useAuth();
  
  const [offer, setOffer] = useState(null);
  const [categories, setCategories] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [priceFrom, setPriceFrom] = useState('');
  const [priceTo, setPriceTo] = useState('');
  const [deadline, setDeadline] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);

  const hasSub = user?.subscription?.active && user?.subscription?.expires_at && new Date(user.subscription.expires_at) > new Date();
  const isOwner = user && offer && user.id === offer.user_id;

  useEffect(() => {
    async function fetchData() {
      try {
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const offerRes = await fetch(`${API}/offers/${offerId}`, { headers, credentials: 'include' });
        const categoriesRes = await fetch(`${API}/categories`);
        if (offerRes.ok) {
          const data = await offerRes.json();
          setOffer(data);
          if (data.images && data.images.length > 0) setSelectedImage(data.images[0]);
          
          // Pobierz odpowiedzi jeśli użytkownik jest autorem ogłoszenia
          if (token && user && data.user_id === user.id) {
            const responsesRes = await fetch(`${API}/offers/${offerId}/responses`, { 
              headers: { 'Authorization': `Bearer ${token}` }, 
              credentials: 'include' 
            });
            if (responsesRes.ok) {
              const respData = await responsesRes.json();
              setResponses(respData.responses || respData || []);
            }
          }
        } else navigate('/offers');
        if (categoriesRes.ok) setCategories(await categoriesRes.json());
      } catch (error) {
        navigate('/offers');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [offerId, navigate, token, user]);

  async function handleSubmit() {
    if (!message.trim()) { toast.error('Wiadomość wymagana'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/offer-responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({
          offer_id: offerId,
          message: message,
          price_from: priceFrom ? parseFloat(priceFrom) : null,
          price_to: priceTo ? parseFloat(priceTo) : null,
          proposed_deadline: deadline || null,
        }),
      });
      if (res.ok) {
        // Śledź konwersję wysłania oferty w Google Ads
        trackOfferResponse(offerId);
        
        toast.success('Oferta wysłana!');
        setShowForm(false);
        setMessage('');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Błąd');
      }
    } catch (error) {
      toast.error('Błąd');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!offer) return null;

  const categoryName = categories.find(c => c.id === offer.category)?.name || offer.category;
  const images = offer.images || [];
  
  // SEO data
  const breadcrumbs = [
    { name: 'Strona główna', path: '/' },
    { name: 'Ogłoszenia', path: '/offers' },
    { name: categoryName, path: `/offers?category=${offer.category}` },
    { name: offer.title, path: `/offers/${offer.id}` }
  ];
  
  const structuredData = [
    getJobPostingSchema(offer),
    getBreadcrumbSchema(breadcrumbs)
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="offer-details-page">
      <SEO 
        title={`${offer.title} - ${categoryName}`}
        description={`${offer.description?.substring(0, 150)}... Lokalizacja: ${offer.location}. Znajdź więcej zleceń budowlanych na Giełda Budowlana.`}
        canonical={`/offers/${offer.id}`}
        image={images[0] || undefined}
        type="article"
        structuredData={structuredData}
      />
      
      <div className="bg-slate-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button variant="ghost" onClick={() => navigate('/offers')} className="text-slate-400 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />Powrót
          </Button>
          <Badge variant="secondary" className="mb-4">{categoryName}</Badge>
          <h1 className="font-heading font-bold text-2xl text-white mb-2">{offer.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-slate-400 text-sm">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{offer.location}</span>
            {offer.deadline && <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(offer.deadline).toLocaleDateString('pl-PL')}</span>}
            <span className="flex items-center gap-1"><User className="w-4 h-4" />{offer.user_name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {images.length > 0 && (
              <Card>
                <CardHeader><CardTitle>Zdjęcia</CardTitle></CardHeader>
                <CardContent>
                  {selectedImage && (
                    <div className="mb-4">
                      <MainImage src={selectedImage} alt="Zdjęcie ogłoszenia" />
                    </div>
                  )}
                  <div className="grid grid-cols-4 gap-2">
                    {images.map((img, idx) => (
                      <ImageItem key={idx} src={img} index={idx} onClick={setSelectedImage} selected={selectedImage === img} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader><CardTitle>Opis zlecenia</CardTitle></CardHeader>
              <CardContent><p className="text-slate-700 whitespace-pre-wrap">{offer.description}</p></CardContent>
            </Card>

            {offer.location && (
              <Card>
                <CardHeader><CardTitle>Lokalizacja</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-slate-700 mb-4 font-medium">{offer.location}</p>
                  <a 
                    href={`https://www.openstreetmap.org/search?query=${encodeURIComponent(offer.location + ', Polska')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-orange-600 hover:underline"
                  >
                    Zobacz na mapie →
                  </a>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            <Card className="border-l-4 border-blue-500">
              <CardContent className="p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-blue-500" />Dane kontaktowe
                </h3>
                
                {/* Autor ogłoszenia zawsze widzi swoje dane */}
                {isAuthenticated && user?.user_id === offer.user_id ? (
                  <div className="space-y-3">
                    {offer.contact_phone && (
                      <div className="flex items-center gap-2 text-slate-700">
                        <Phone className="w-4 h-4" />{offer.contact_phone}
                      </div>
                    )}
                    {offer.contact_email && (
                      <div className="flex items-center gap-2 text-slate-700">
                        <Mail className="w-4 h-4 flex-shrink-0" /><span className="break-all">{offer.contact_email}</span>
                      </div>
                    )}
                    {!offer.contact_phone && !offer.contact_email && (
                      <p className="text-slate-500 text-sm">Brak danych kontaktowych</p>
                    )}
                    <p className="text-xs text-slate-400 mt-2">To Twoje ogłoszenie</p>
                  </div>
                ) : FREE_MODE ? (
                  /* Wersja DARMOWA: dane kontaktowe dostępne dla wszystkich firm */
                  isAuthenticated && isCompany ? (
                    <div className="space-y-3">
                      {offer.contact_phone && (
                        <a href={`tel:${offer.contact_phone}`} className="flex items-center gap-2 text-slate-700 hover:text-orange-600">
                          <Phone className="w-4 h-4" />{offer.contact_phone}
                        </a>
                      )}
                      {offer.contact_email && (
                        <a href={`mailto:${offer.contact_email}`} className="flex items-center gap-2 text-slate-700 hover:text-orange-600">
                          <Mail className="w-4 h-4 flex-shrink-0" /><span className="break-all">{offer.contact_email}</span>
                        </a>
                      )}
                      {!offer.contact_phone && !offer.contact_email && (
                        <p className="text-slate-500 text-sm">Brak danych kontaktowych</p>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <Lock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-600">Dane kontaktowe dostępne tylko dla firm</p>
                      {!isAuthenticated && (
                        <Button onClick={() => navigate('/login')} size="sm" className="mt-3 bg-orange-500 hover:bg-orange-600">
                          Zaloguj się jako firma
                        </Button>
                      )}
                    </div>
                  )
                ) : (
                  /* Wersja PŁATNA: dane kontaktowe tylko dla firm z subskrypcją */
                  isAuthenticated && isCompany && hasSub ? (
                    <div className="space-y-3">
                      {offer.contact_phone && (
                        <a href={`tel:${offer.contact_phone}`} className="flex items-center gap-2 text-slate-700 hover:text-orange-600">
                          <Phone className="w-4 h-4" />{offer.contact_phone}
                        </a>
                      )}
                      {offer.contact_email && (
                        <a href={`mailto:${offer.contact_email}`} className="flex items-center gap-2 text-slate-700 hover:text-orange-600">
                          <Mail className="w-4 h-4" />{offer.contact_email}
                        </a>
                      )}
                      {!offer.contact_phone && !offer.contact_email && (
                        <p className="text-slate-500 text-sm">Brak danych kontaktowych</p>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <Lock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-600 mb-3">Dane kontaktowe dostępne tylko dla firm z subskrypcją</p>
                      {isAuthenticated && isCompany && !hasSub && (
                        <Link to="/subscription">
                          <Button size="sm" className="bg-orange-500 hover:bg-orange-600">
                            <CreditCard className="w-4 h-4 mr-2" />Aktywuj subskrypcję
                          </Button>
                        </Link>
                      )}
                      {!isAuthenticated && (
                        <Button onClick={() => navigate('/login')} size="sm" className="bg-orange-500 hover:bg-orange-600">
                          Zaloguj się
                        </Button>
                      )}
                    </div>
                  )
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="w-5 h-5 text-orange-500" />
                  <span className="font-bold">Odpowiedz na zlecenie</span>
                </div>
                
                {!isAuthenticated && (
                  <div className="text-center">
                    <p className="text-sm text-slate-600 mb-4">Zaloguj się jako firma</p>
                    <Button onClick={() => navigate('/login')} className="w-full bg-orange-500 hover:bg-orange-600">Zaloguj się</Button>
                  </div>
                )}
                
                {isAuthenticated && !isCompany && (
                  <div className="text-center text-sm text-slate-600">
                    <AlertCircle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    Tylko firmy mogą odpowiadać
                  </div>
                )}
                
                {/* Blokada dla firm bez subskrypcji - tylko w trybie płatnym */}
                {!FREE_MODE && isAuthenticated && isCompany && !hasSub && !showForm && (
                  <div className="text-center">
                    <AlertCircle className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600 mb-4">Aby wysyłać oferty, aktywuj subskrypcję</p>
                    <Link to="/subscription">
                      <Button className="w-full bg-orange-500 hover:bg-orange-600">
                        <CreditCard className="w-4 h-4 mr-2" />Włącz subskrypcję
                      </Button>
                    </Link>
                  </div>
                )}
                
                {/* Przycisk odpowiedzi - w trybie darmowym lub dla firm z subskrypcją */}
                {isAuthenticated && isCompany && (FREE_MODE || hasSub) && !showForm && (
                  <div className="space-y-3">
                    <Button onClick={() => setShowForm(true)} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="respond-btn">
                      <Send className="w-4 h-4 mr-2" />Wyślij zapytanie
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => navigate(`/chat?user=${offer.user_id}&offer=${offerId}`)} 
                      className="w-full"
                      data-testid="start-chat-btn"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />Napisz wiadomość
                    </Button>
                  </div>
                )}
                
                {isAuthenticated && isCompany && (FREE_MODE || hasSub) && showForm && (
                  <div className="space-y-4">
                    <div>
                      <Label>Wiadomość *</Label>
                      <Textarea 
                        value={message} 
                        onChange={(e) => setMessage(e.target.value)} 
                        rows={4} 
                        placeholder="Opisz swoją ofertę, doświadczenie i podejście do zlecenia..."
                      />
                    </div>
                    <div>
                      <Label>Widełki cenowe (PLN)</Label>
                      <div className="grid grid-cols-2 gap-3 mt-1">
                        <div>
                          <Input 
                            type="number" 
                            value={priceFrom} 
                            onChange={(e) => setPriceFrom(e.target.value)} 
                            placeholder="Cena od"
                            min="0"
                          />
                        </div>
                        <div>
                          <Input 
                            type="number" 
                            value={priceTo} 
                            onChange={(e) => setPriceTo(e.target.value)} 
                            placeholder="Cena do"
                            min="0"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Podaj szacunkowy zakres cenowy za wykonanie zlecenia</p>
                    </div>
                    <div>
                      <Label>Proponowany termin realizacji</Label>
                      <Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
                    </div>
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">Anuluj</Button>
                      <Button onClick={handleSubmit} disabled={submitting} className="flex-1 bg-orange-500 hover:bg-orange-600">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Wyślij ofertę'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="font-bold text-2xl text-slate-900">{offer.responses_count}</p>
                    <p className="text-sm text-slate-500">Ofert</p>
                  </div>
                  <div>
                    <p className="font-bold text-lg text-slate-900">{new Date(offer.created_at).toLocaleDateString('pl-PL')}</p>
                    <p className="text-sm text-slate-500">Dodano</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Sekcja odpowiedzi - widoczna tylko dla autora ogłoszenia */}
        {isOwner && responses.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Otrzymane oferty ({responses.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {responses.map((resp) => (
                    <div key={resp.response_id || resp.id} className="border border-slate-200 rounded-lg p-4 hover:border-orange-200 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{resp.company_name || resp.responder_name || 'Firma'}</p>
                            <p className="text-xs text-slate-500">{new Date(resp.created_at).toLocaleDateString('pl-PL')}</p>
                          </div>
                        </div>
                        {(resp.price_from || resp.price_to) && (
                          <Badge className="bg-green-100 text-green-700">
                            {resp.price_from && resp.price_to 
                              ? `${resp.price_from} - ${resp.price_to} PLN` 
                              : resp.price_from 
                                ? `od ${resp.price_from} PLN` 
                                : `do ${resp.price_to} PLN`}
                          </Badge>
                        )}
                      </div>
                      {resp.message && (
                        <p className="text-slate-600 text-sm mb-3">{resp.message}</p>
                      )}
                      {resp.proposed_deadline && (
                        <p className="text-xs text-slate-500">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          Proponowany termin: {new Date(resp.proposed_deadline).toLocaleDateString('pl-PL')}
                        </p>
                      )}
                      {resp.responder_id && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <Link 
                            to={`/messages?user=${resp.responder_id}`}
                            className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                          >
                            <Send className="w-4 h-4 inline mr-1" />
                            Napisz wiadomość
                          </Link>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
